import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Play, Disc3, User, Film, Tv } from 'lucide-react'
import { albums, artists, tracks, discovery, videos, series } from '../api/client'
import { usePlayerStore } from '../stores/player'
import type { TrackWithDetails, Artist, Video, SeriesWithEpisodes } from '../types'

function RecentTrackCard({ track, onPlay }: { track: TrackWithDetails; onPlay: () => void }) {
  return (
    <div
      onClick={onPlay}
      className="flex-shrink-0 w-32 sm:w-40 group cursor-pointer"
    >
      <div className="aspect-square mb-2 bg-dark-700 rounded-lg overflow-hidden relative">
        {track.cover_path ? (
          <img
            src={`/api/image/${encodeURIComponent(track.cover_path)}?size=thumbnail`}
            alt={track.title}
            className="w-full h-full object-cover" loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc3 size={32} className="text-dark-500" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Play size={24} className="text-white" fill="white" />
        </div>
      </div>
      <p className="text-sm font-medium truncate group-hover:text-primary-400">
        {track.title}
      </p>
      <p className="text-xs text-dark-400 truncate">{track.artist_name}</p>
    </div>
  )
}

function ArtistCard({ artist }: { artist: Artist }) {
  return (
    <Link
      to={`/artists/${artist.id}`}
      className="flex-shrink-0 w-28 sm:w-36 group"
    >
      <div className="aspect-square mb-2 bg-dark-700 rounded-full overflow-hidden">
        {artist.image_path ? (
          <img
            src={`/api/image/${encodeURIComponent(artist.image_path)}`}
            alt={artist.name}
            className="w-full h-full object-cover" loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User size={32} className="text-dark-500" />
          </div>
        )}
      </div>
      <p className="text-center text-sm font-medium truncate group-hover:text-primary-400">
        {artist.name}
      </p>
    </Link>
  )
}

function AlbumCard({ album }: { album: { id: string; title: string; artist_name?: string | null; cover_path?: string | null } }) {
  return (
    <Link
      to={`/albums/${album.id}`}
      className="flex-shrink-0 w-32 sm:w-40 group"
    >
      <div className="aspect-square mb-2 bg-dark-700 rounded-lg overflow-hidden">
        {album.cover_path ? (
          <img
            src={`/api/image/${encodeURIComponent(album.cover_path)}?size=thumbnail`}
            alt={album.title}
            className="w-full h-full object-cover" loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc3 size={32} className="text-dark-500" />
          </div>
        )}
      </div>
      <p className="text-sm font-medium truncate group-hover:text-primary-400">
        {album.title}
      </p>
      {album.artist_name && (
        <p className="text-xs text-dark-400 truncate">{album.artist_name}</p>
      )}
    </Link>
  )
}

function MovieCard({ movie }: { movie: Video }) {
  return (
    <Link
      to={`/movies/${movie.id}`}
      className="flex-shrink-0 w-28 sm:w-32 group"
    >
      <div className="aspect-[2/3] mb-2 bg-dark-700 rounded-lg overflow-hidden">
        {movie.thumbnail_path ? (
          <img
            src={`/api/image/${encodeURIComponent(movie.thumbnail_path)}?size=poster`}
            alt={movie.title}
            className="w-full h-full object-cover" loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film size={32} className="text-dark-500" />
          </div>
        )}
      </div>
      <p className="text-sm font-medium truncate group-hover:text-primary-400">
        {movie.title}
      </p>
      {movie.year && (
        <p className="text-xs text-dark-400">{movie.year}</p>
      )}
    </Link>
  )
}

function SeriesCard({ show }: { show: SeriesWithEpisodes }) {
  return (
    <Link
      to={`/series/${show.id}`}
      className="flex-shrink-0 w-28 sm:w-32 group"
    >
      <div className="aspect-[2/3] mb-2 bg-dark-700 rounded-lg overflow-hidden">
        {show.poster_path ? (
          <img
            src={`/api/image/${encodeURIComponent(show.poster_path)}?size=poster`}
            alt={show.title}
            className="w-full h-full object-cover" loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Tv size={32} className="text-dark-500" />
          </div>
        )}
      </div>
      <p className="text-sm font-medium truncate group-hover:text-primary-400">
        {show.title}
      </p>
      <p className="text-xs text-dark-400">
        {show.seasons.length} season{show.seasons.length !== 1 ? 's' : ''} · {show.episode_count} ep
      </p>
    </Link>
  )
}

export default function Home() {
  const { playQueue } = usePlayerStore()

  const { data: recentTracks } = useQuery({
    queryKey: ['tracks', 'recent'],
    queryFn: () => tracks.recent(20),
  })

  const { data: recentAlbums } = useQuery({
    queryKey: ['albums', 'recent'],
    queryFn: () => albums.list(12, 0),
  })

  const { data: allArtists } = useQuery({
    queryKey: ['artists', 'recent'],
    queryFn: () => artists.list(12, 0),
  })

  const { data: recentMovies } = useQuery({
    queryKey: ['movies', 'recent'],
    queryFn: () => videos.listMovies(12, 0),
  })

  const { data: allSeries } = useQuery({
    queryKey: ['series', 'recent'],
    queryFn: () => series.list(12, 0),
  })

  const { data: discoverySections } = useQuery({
    queryKey: ['discovery'],
    queryFn: () => discovery.get(),
  })

  const handlePlayTrack = (track: TrackWithDetails, trackList: TrackWithDetails[]) => {
    const index = trackList.findIndex(t => t.id === track.id)
    playQueue(trackList, index)
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
      <h1 className="text-2xl sm:text-3xl font-bold">Home</h1>

      {/* Discovery Sections */}
      {discoverySections && discoverySections.length > 0 && (
        <div className="space-y-6">
          {discoverySections.map((section, index) => (
            <section key={index}>
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{section.title}</h2>
              <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
                {section.recommended_artists.map((artist) => (
                  <ArtistCard key={artist.id} artist={artist} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Recently Added Tracks */}
      {recentTracks && recentTracks.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">Recently Added</h2>
            <Link to="/songs" className="text-sm text-primary-400 hover:text-primary-300">
              View all
            </Link>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {recentTracks.slice(0, 12).map((track) => (
              <RecentTrackCard
                key={track.id}
                track={track}
                onPlay={() => handlePlayTrack(track, recentTracks)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Recent Albums */}
      {recentAlbums && recentAlbums.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">Albums</h2>
            <Link to="/albums" className="text-sm text-primary-400 hover:text-primary-300">
              View all
            </Link>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {recentAlbums.map((album) => (
              <AlbumCard key={album.id} album={album} />
            ))}
          </div>
        </section>
      )}

      {/* Artists */}
      {allArtists && allArtists.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">Artists</h2>
            <Link to="/artists" className="text-sm text-primary-400 hover:text-primary-300">
              View all
            </Link>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {allArtists.map((artist) => (
              <ArtistCard key={artist.id} artist={artist} />
            ))}
          </div>
        </section>
      )}

      {/* Recent Movies */}
      {recentMovies && recentMovies.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">Movies</h2>
            <Link to="/movies" className="text-sm text-primary-400 hover:text-primary-300">
              View all
            </Link>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {recentMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}

      {/* TV Shows */}
      {allSeries && allSeries.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">TV Shows</h2>
            <Link to="/tv" className="text-sm text-primary-400 hover:text-primary-300">
              View all
            </Link>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {allSeries.map((show) => (
              <SeriesCard key={show.id} show={show} />
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {(!recentTracks || recentTracks.length === 0) &&
       (!recentAlbums || recentAlbums.length === 0) && (
        <div className="card text-center py-12">
          <p className="text-dark-400 mb-2">Your library is empty</p>
          <p className="text-dark-500 text-sm">
            Add some media libraries to get started
          </p>
          <Link
            to="/libraries"
            className="inline-block mt-4 btn btn-primary"
          >
            Add Library
          </Link>
        </div>
      )}
    </div>
  )
}
